import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { Button, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/clientProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { ClientService } from '../../../services/client/clientService';
import { ClientApiResponse } from '../../../type/entities/client-type';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.clients.profile.info,
        permission: {
            group: 'clients',
            action: 'get',
        },
    }
];

export default function ClientProfileLayout({ isLoading, children }: { isLoading?: boolean, children: React.ReactNode }) {

    const { selectedView } = useSelector((state: RootState) => state.clientProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { handleErrors } = useHandleErrors();
    const service = new ClientService();


    const [changingStatus, setChangingStatus] = useState<string[]>([]);

    const [data, loadingClient, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getClientById(id);
        return response.getResponseData() as ClientApiResponse;
    }, [id]));

    const pageHeader = {
        title: `Perfil de ${data?.name || 'Cliente'}`,
        breadcrumb: [
            { name: 'Clientes' },
            { name: 'Clientes', href: menuRoutes.clients.path },
            { name: 'Perfil de Cliente' },
        ],
    };
  

    const toggleState = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleClientState(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el cliente" : "Se ha activado el cliente");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteClient(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.clients.path);
                toast.success('Cliente eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    useEffect(() => {
        navigate(`${menuRoutes.clients.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {userCan('edit', 'clients') && (
                        <>

                            {data && (
                                <Tooltip
                                    size="sm"
                                    content={data.status ? 'Desactivar cliente' : 'Activar cliente'}
                                    placement="top"
                                    color="invert"
                                >
                                    <Switch
                                        id={id}
                                        label={data.status ? 'Activo' : 'Desactivado'}
                                        checked={data.status}
                                        onChange={() => toggleState(data.id, data.status)}
                                        switchKnobClassName='bg-red-300'
                                    />
                                </Tooltip>
                            )}

                            <div className='border-l border-gray-300' />

                            <Tooltip content={'Editar cliente'} placement={'top'} color={'invert'} size={'sm'}>
                                <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                    navigate(`${menuRoutes.clients.path}/${id}/edit`)
                                }}>
                                    Editar
                                </Button>
                            </Tooltip>
                        </>
                    )}

                    {userCan('delete', 'clients') && (
                        <DeletePopover
                            title={`Eliminar cliente`}
                            description={`¿Estás seguro de que deseas eliminar el cliente?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10'
                        />
                    )}
                </div>
            </PageHeader>
            


            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}
        </>
    );
}